import {
    Apartment,
    AssessmentRounded,
    Badge,
    ReceiptRounded,
    Settings,
} from '@mui/icons-material';
import { Box, List, ListItem, Skeleton, Typography } from '@mui/material';
import { MouseEventHandler, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { getSelectedPropertyId } from 'entities/Property/model/selectors/PropertySelectors';
import { UserRole, getUserRole } from 'entities/User';
import { getAuthIsLoading } from 'entities/User/model/selectors/userSelectors';
import { HelpMenu } from 'features/HelpMenu';
import { useNavigationBlock } from 'features/NavigationBlockModal/ui/NavigationBlockProvider/NavigationBlockProvider';
import { shouldShowVersionInfo } from 'shared/const/envVariables';
import {
    getRouteMainReports,
    getRouteMainVendors,
    getRouteOrders,
    getRouteProperties,
    getRouteSettings,
} from 'shared/const/router';
import { contentHeight } from 'shared/const/styles';
import { AppLink } from 'shared/ui/AppLink/AppLink';
import { VersionInfo } from 'widgets/VersionInfo';

export const Navbar = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { validateNavBlock } = useNavigationBlock();

    const userRole = useSelector(getUserRole);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const userIsLoading = useSelector(getAuthIsLoading);

    const navBarItemsList = useMemo(
        () => [
            {
                label: t('Properties'),
                route: getRouteProperties(),
                icon: Apartment,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
            },
            {
                label: t('Orders'),
                route: getRouteOrders(),
                icon: ReceiptRounded,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
            },
            {
                label: t('Vendors'),
                route: getRouteMainVendors(selectedPropertyId),
                icon: Badge,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_TECHNICIAN,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
            },
            {
                label: t('Reports'),
                route: getRouteMainReports(),
                icon: AssessmentRounded,
                roles: [
                    UserRole.PMC_ADMIN,
                    UserRole.PROPERTY_MANAGER,
                    UserRole.MAINTENANCE_SUPERVISOR,
                ],
            },
            {
                label: t('Settings'),
                route: getRouteSettings(),
                icon: Settings,
                roles: [UserRole.PMC_ADMIN, UserRole.PROPERTY_MANAGER],
            },
        ],
        [t, selectedPropertyId],
    );

    return (
        <>
            <Box
                bgcolor="background.paper"
                height={contentHeight}
                minWidth="200px"
                maxWidth="260px"
                position="fixed"
                zIndex="drawer"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
            >
                <List sx={{ pt: '24px' }}>
                    {!userIsLoading ? (
                        navBarItemsList.map((item) => {
                            const isActive = matchPath(
                                item.route + '/*',
                                location.pathname,
                            );

                            const isHidden =
                                item?.roles &&
                                item?.roles?.includes(userRole) === false;
                            const Icon = item.icon;

                            const disabledClick: MouseEventHandler<
                                HTMLAnchorElement
                            > = (e) => {
                                e.preventDefault();
                            };

                            if (isHidden) {
                                return null;
                            }

                            return (
                                <ListItem
                                    data-testid={`menu-${item.label.toLowerCase()}`}
                                    sx={{
                                        p: 0,
                                        pl: '24px',
                                        ':hover': !isActive && {
                                            bgcolor: 'action.hover',
                                            cursor: 'pointer',
                                        },
                                        bgcolor: isActive && 'primary.main',
                                        color: isActive && 'common.white',
                                    }}
                                    key={item.route}
                                >
                                    <Box
                                        component={isActive ? 'div' : AppLink}
                                        onClick={
                                            isActive
                                                ? disabledClick
                                                : (e: React.MouseEvent) => {
                                                      e.preventDefault();
                                                      validateNavBlock(() => {
                                                          navigate(item.route);
                                                      }, true);
                                                  }
                                        }
                                        to={isActive ? undefined : item.route}
                                        display="flex"
                                        gap="12px"
                                        alignItems="center"
                                        p="12px 0"
                                        width="100%"
                                        sx={
                                            isActive
                                                ? {}
                                                : {
                                                      cursor: 'pointer',
                                                      textDecoration: 'none',
                                                  }
                                        }
                                    >
                                        <Icon
                                            sx={{
                                                color: isActive
                                                    ? 'common.white'
                                                    : 'action.active',
                                            }}
                                        />
                                        <Typography
                                            color={
                                                isActive
                                                    ? 'common.white'
                                                    : 'action.active'
                                            }
                                        >
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </ListItem>
                            );
                        })
                    ) : (
                        <Box display="flex" flexDirection="column" gap="2px">
                            <Skeleton variant="rectangular" height="48px" />
                            <Skeleton variant="rectangular" height="48px" />
                            <Skeleton variant="rectangular" height="48px" />
                        </Box>
                    )}
                </List>
                <Box p="0 0 12px 0">
                    <HelpMenu />
                    {shouldShowVersionInfo() && (
                        <Typography
                            marginTop="12px"
                            marginRight="24px"
                            align="center"
                            style={{ opacity: '0.5' }}
                        >
                            <VersionInfo />
                        </Typography>
                    )}
                </Box>
            </Box>
            <Box height={contentHeight} minWidth="200px" maxWidth="260px" />
        </>
    );
};
